import { action, computed, observable, reaction, makeObservable } from 'mobx';
import { X5LessonSnapshot, X8BlockSnapshot } from '../../../domain';
import { Disposers, HttpPromiseState, logger } from '../../../lib/helpers';
import { Homework } from '../homework-models';
import { HomeworkUtils } from '../homework-utils';
import { HomeworkLoaders } from '../stores/homework-loaders';
import { HomeworkHubStore } from '../stores/HomeworkHubStore';

export class StudentHomeworkSceneStore {
  @observable.ref
  homeworkLoader: HttpPromiseState<Homework> | null;

  hubStore: HomeworkHubStore;

  @observable.ref
  snapshotState: HttpPromiseState<X5LessonSnapshot> | null;

  @observable.ref
  snapshot: X5LessonSnapshot | null;

  private readonly disposers = new Disposers();

  constructor(readonly homeworkId: number) {
    makeObservable(this);
    this.homeworkLoader = null;

    this.hubStore = new HomeworkHubStore(homeworkId, 'Student');
    this.disposers.add(this.hubStore);

    this.snapshotState = this.snapshot = null;
    this.setup();
  }

  private setup() {
    this.disposers.add(
      reaction(
        () => this.homeworkLoader?.value,
        this.onHomeworkLoaded),
      reaction(
        () => this.snapshotState?.value,
        this.onLessonLoaded),
      reaction(
        () => [this.snapshot, this.hubStore.tasks.active, this.hubStore.keyValues.initialized],
        this.initializeContentItems),
    );
  }

  @action
  loadHomework = () => {
    if (this.homeworkLoader?.isPending)
      return;
    this.homeworkLoader = HomeworkLoaders.Student.getHomework(this.homeworkId);
  };

  @action
  private onHomeworkLoaded = () => {
    const state = this.homeworkLoader;
    if (state === null || !state.isResolved)
      return;
    this.hubStore.start();
    this.loadLesson();
  };

  @computed
  get canConnectToHub(): boolean {
    return (!this.hubStore.connection.dontReconnect);
  }

  @action
  loadLesson = () => {
    if (this.snapshotState?.isPending)
      return;
    if (this.snapshot?.Id === this.hubStore.lessonSnapshotId)
      return;
    this.snapshotState = HomeworkLoaders.Student.getLessonSnapshot(this.homeworkId);
  };

  @action
  private onLessonLoaded = () => {
    const state = this.snapshotState;
    if (state === null || !state.isResolved)
      return;
    this.snapshot = HomeworkUtils.prepareLesson(state.value);
  };

  @action
  private initializeContentItems = () => {
    if (this.snapshot === null)
      return;
    if (!this.hubStore.tasks.active || !this.hubStore.keyValues.initialized)
      return;
    logger.log('StudentHomeworkSceneStore.initializeContentItems');
    if (this.hubStore.keyValues.get<boolean>('initialized') === true)
      return;
    HomeworkUtils.initializeLesson(this.hubStore.keyValues, this.snapshot);
    this.hubStore.keyValues.set<boolean>('initialized', true);
  };

  isRevealable(block: X8BlockSnapshot): boolean {
    return (block.BlockType === 'Pushable');
  }

  isRevealed(block: X8BlockSnapshot): boolean {
    return this.hubStore.revealedBlockIds.has(block.Id);
  }

  dispose() {
    this.disposers.dispose();
  }
}
